import _ from "lodash";
import React, { useRef } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import classes from "./UnitSelection.scss";
import { getUnitTypeOptionsMemoized } from "UnitPlans/modules/UnitPlanModule";
import I18nHOC from "UIComponents/I18nHOC";
import PaginationLoader from "UIComponents/PaginationLoader";
import {
  getOrganizationUnitPlanFeedQuery,
  getUnitSectionFilterDataQuery,
} from "../modules/AssessmentSelectModalQueries";
import {
  getOrganizationUnitPlanFeedV2FromCache,
  getUnitSectionFilterDataFromCache,
} from "../modules/AssessmentSelectModalGraphqlHelpers";
import { withRouter } from "react-router";
import { prepareLoadMoreFeed, getRethumbUrl } from "Utils";
import {
  getFeedlistColumnsMemoized,
  getUnitPlanFeedDataMemoized,
  getFilterInfoMemoized,
} from "./UnitSelectionUtils";
import { useHandleScroll, useMount } from "Utils/CustomHooks";
import { RadioButton, Tooltip } from "@toddle-edu/ds-web";
import {
  getDefaultUnitPlanImage,
  getUnitPlanFieldResponseFilter,
} from "UnitPlans/modules/Utils";
import classNames from "classnames";
import { getUserInfo, getSampleOrgConfig } from "Login/modules/LoginModule";
import { getUnitPlanDetailsFromCache } from "IBPlanner/modules/IBGraphqlHelpers";
import { getUnitPlanCentralRepoInputAndFilters } from "Teacher/routes/Courses/modules/utils";
import FilterHeader from "Course/components/FilterHeader";
import FeedListv2 from "AppComponents/FeedListv2";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";

const getRethumb = imageUrl => {
  const rethumbUrl = imageUrl
    ? getRethumbUrl({
        width: 160,
        height: 192,
        imageUrl: imageUrl,
        fitIn: false,
      })
    : "";
  return `url(${rethumbUrl})`;
};

const getCustomComponent = (
  { column, columnData },
  t,
  selectedItems,
  testId
) => {
  switch (column.id) {
    case "radioButton":
      return (
        <RadioButton
          checked={columnData == _.get(selectedItems, "unitPlan.id", null)}
          testId={`${testId}-${columnData}`}
        />
      );
    case "unitName": {
      return (
        <div className={classes.unitNameContainer}>
          <div
            className={classes.imageContainer}
            style={{
              backgroundImage: getDefaultUnitPlanImage({
                id: columnData.id,
                imageUrl: columnData.image,
                getRethumb,
              }),
            }}
          />
          <Tooltip tooltip={columnData.title}>
            <div className={classNames(classes.cardTitle, "text-body-1")}>
              {columnData.title}
            </div>
          </Tooltip>
        </div>
      );
    }
    default:
      return null;
  }
};

const UnitSelection = props => {
  const {
    grades = [],
    subjects = [],
    unitPlansCount,
    isData,
    isLoading,
    gradeList,
    organizationId,
    archivalState,
    t,
    hasNextPage,
    networkStatus,
    loadMoreFeed,
    isFetchingMoreData,
    subjectsList,
    feedData = [],
    currentCurriculumType,
    unitTypeOptions,
    unitType = [],
    unitPlans,
    updateUnitPlan,
    selectedItems,
    filterList,
    courseList = [],
    updateUnitSelectionFilters,
    selectedCourses,
    unitSelectionFilters,
    selectedCurriculumProgramIds,
    modalType,
    testId = "unitPlan",
    excludedUnitPlanIds = [],
  } = props;

  const scrollContainerRef = useRef({});

  useMount(() => {
    if (_.isEmpty(unitSelectionFilters)) {
      if (_.isEqual(modalType, "ADD_FROM_TODDLE_LIBRARY")) {
        updateUnitSelectionFilters({
          courses: [
            _.get(
              _.find(getSampleOrgConfig(), {
                curriculumType: currentCurriculumType,
              }),
              "demoCourse",
              ""
            ),
          ],
        });
      } else {
        updateUnitSelectionFilters({
          grades,
          subjects,
          unitType,
          courses: selectedCourses,
          curriculumProgramIds: selectedCurriculumProgramIds,
          excludedUnitPlanIds,
        });
      }
    }
  });

  const getScrollRef = () => {
    return scrollContainerRef.current;
  };

  useHandleScroll({
    networkStatus,
    hasNextPage,
    getScrollRef,
    loadMoreFeed,
  });

  const onFilterChange = params => {
    updateUnitSelectionFilters(params);
  };

  const onItemClick = id => {
    const selectedUnitPlan = _.find(unitPlans, unitPlan => {
      return _.get(unitPlan, "node.id", "") == id;
    });
    updateUnitPlan({ unitPlan: _.get(selectedUnitPlan, "node") });
  };

  return (
    <div className={classes.modalContainer}>
      <div className={classes.filterHeader}>
        <FilterHeader
          filtersInfo={getFilterInfoMemoized({
            gradeList,
            t,
            organizationId,
            archivalState,
            subjectsList,
            currentCurriculumType,
            unitTypeOptions,
            filterList,
            courseList,
          })}
          filtersValue={unitSelectionFilters}
          onFilterChange={onFilterChange}
          isFilterDropdownV2={true}
          testId={`${testId}-assessmentImportModal`}
        />
      </div>
      <div className="text-label-1">
        {t("common:unit_with_count", {
          count: unitPlansCount,
        })}
      </div>
      <div className={classes.feedListContainer} ref={scrollContainerRef}>
        <div>
          <FeedListv2
            columnList={getFeedlistColumnsMemoized({
              modalType: modalType,
              t,
              curriculumType: currentCurriculumType,
            })}
            feedData={feedData}
            testId={`${testId}-assessmentImportModal`}
            onItemClick={onItemClick}
            isItemClickEnable={true}
            getCustomComponent={(...params) =>
              getCustomComponent(...params, t, selectedItems, testId)
            }
            isData={isData}
            isLoading={isLoading}
            scrollContainerRef={scrollContainerRef}
            enableStickyHeader={true}
          />
          {isFetchingMoreData ? <PaginationLoader /> : null}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { t, portalType, modalType } = ownProps;
  const userInfo = getUserInfo({ portalType });
  const currentUnitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
  const selected_class = state.teacher.selected_class;
  const academicYearId = getSelectedAcademicYearId(state);
  const organizationId = userInfo.org_id;

  const { centralRepoInput } = getUnitPlanCentralRepoInputAndFilters({
    organizationId: organizationId,
    courseId: _.get(selected_class, "courseId", ""),
    academicYearId,
  });

  const responseFilter = getUnitPlanFieldResponseFilter();
  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  const unitPlanDetails = getUnitPlanDetailsFromCache({
    unitPlanId: currentUnitPlanId,
    centralRepoInput,
    responseFilter,
    collaboratorsFilter,
    exceptionalCollaboratorsFilter: collaboratorsFilter,
  });

  let selectedCourses = [];
  let selectedCurriculumProgramIds = [];
  const curriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id",
    null
  );
  const currentCurriculumType = _.get(
    state,
    "platform.currentCurriculumProgram.type",
    null
  );
  const curriculumProgramIds = curriculumProgramId ? [curriculumProgramId] : [];
  const coursesFilters = {
    curriculumProgramIds,
  };

  if (_.isEqual(modalType, "ADD_FROM_OTHER_UNIT")) {
    const currentUnitPlanAcademicYear = _.first(
      _.get(unitPlanDetails, "academicYears", [])
    );
    coursesFilters.academicYears = currentUnitPlanAcademicYear
      ? [currentUnitPlanAcademicYear.id]
      : [];
  }
  const unitTypeOptions = getUnitTypeOptionsMemoized({
    organizationId: state.login.userInfo.org_id,
    curriculumType: currentCurriculumType,
    t,
  });

  if (_.isEqual(modalType, "ADD_FROM_OTHER_UNIT")) {
    selectedCourses = _.map(
      _.get(unitPlanDetails, "courses", []),
      course => course.id
    );
    selectedCurriculumProgramIds = curriculumProgramIds;
  }
  return {
    organizationId,
    isData: true,
    isLoading: false,
    curriculumProgramIds,
    currentCurriculumType,
    unitTypeOptions,
    selectedCourses,
    selectedCurriculumProgramIds,
    coursesFilters,
  };
};

const getUnitPlanFeedQueryFilters = ({
  unitSelectionFilters,
  courseList,
  unitTypeOptions,
}) => {
  //since we can't change the unitSelectionFilters because it is used to show the filters
  const filters = _.cloneDeep(unitSelectionFilters);
  if (filters?.courses) {
    const isAllCourseListSelected =
      _.size(filters?.courses) === _.size(courseList);
    filters.courses = isAllCourseListSelected ? [] : filters.courses;
  }
  if (filters?.unitType) {
    const isAllUnitTypesSelected =
      _.size(filters?.unitType) === _.size(unitTypeOptions);
    filters.unitType = isAllUnitTypesSelected ? [] : filters.unitType;
  }
  return filters;
};

export default compose(
  I18nHOC({ resource: ["common", "teacherHomePage", "configuration"] }),
  withRouter,
  connect(mapStateToProps, null),
  graphql(getUnitSectionFilterDataQuery, {
    name: "getUnitSectionFilterDataQuery",
    options: ({ organizationId, portalType, coursesFilters, modalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        organizationId,
        portalType,
        coursesFilters,
        skipSubjects: !_.isEqual(modalType, "ADD_FROM_TODDLE_LIBRARY"),
      },
    }),
    props({
      getUnitSectionFilterDataQuery,
      ownProps: {
        organizationId,
        isData,
        isLoading,
        coursesFilters,
        modalType,
      },
    }) {
      const data = getUnitSectionFilterDataFromCache({
        organizationId,
        coursesFilters,
        skipSubjects: !_.isEqual(modalType, "ADD_FROM_TODDLE_LIBRARY"),
      });
      const subjects = _.get(data, "subjects", []);
      const subjectsList = _.map(subjects, subject => ({
        value: subject.id,
        label: subject.name,
      }));
      const gradeList = _.map(_.get(data, "grades", []), grade => {
        return { value: grade.id, label: grade.name };
      });
      const grades = _.map(_.get(data, "grades", []), grade => grade.id);

      const courses = _.get(data, "courses", {});

      const courseList = _.map(_.get(courses, "edges", []), ({ node }) => {
        const { id, title } = node;
        return { key: id, label: title, value: id };
      });

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading:
          isLoading ||
          _.includes([1, 2], getUnitSectionFilterDataQuery["networkStatus"]),
        subjectsList,
        subjects: _.map(subjects, subject => subject.id),
        gradeList,
        grades,
        courseList,
      };
    },
  }),
  graphql(getOrganizationUnitPlanFeedQuery, {
    name: "getOrganizationUnitPlanFeed",
    options: ({
      unitSelectionFilters,
      organizationId,
      portalType,
      courseList,
      unitTypeOptions,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: organizationId,
        filters: getUnitPlanFeedQueryFilters({
          unitSelectionFilters,
          courseList,
          unitTypeOptions,
        }),
        showCollaborators: true,
        portalType,
      },
      context: {
        useHttpLink: true,
      },
    }),
    props({
      getOrganizationUnitPlanFeed,
      ownProps: { isData, isLoading, t, gradeList },
    }) {
      const variables = _.get(getOrganizationUnitPlanFeed, "variables", {});
      const organizationData = getOrganizationUnitPlanFeedV2FromCache(
        variables
      );

      const unitPlansCount = _.get(organizationData, "unitPlans.totalCount", 0);
      const endCursor = _.get(
        getOrganizationUnitPlanFeed,
        "node.unitPlans.pageInfo.endCursor",
        ""
      );
      const hasNextPage = _.get(
        getOrganizationUnitPlanFeed,
        "node.unitPlans.pageInfo.hasNextPage",
        ""
      );
      const feedData = getUnitPlanFeedDataMemoized({
        unitPlans: _.get(organizationData, "unitPlans.edges", []),
        t,
        gradeList,
      });

      return {
        onPusherEventTriggered: () => {
          getOrganizationUnitPlanFeed.refetch();
        },
        unitPlans: _.get(organizationData, "unitPlans.edges", []),
        unitPlansCount,
        feedData,
        hasNextPage,
        loadMoreFeed: prepareLoadMoreFeed({
          variables: {
            ...variables,
            after: endCursor,
          },
          queryData: getOrganizationUnitPlanFeed,
          key: "unitPlans",
        }),
        isFetchingMoreData:
          _.get(getOrganizationUnitPlanFeed, "networkStatus", "") == 3,
        networkStatus: _.get(getOrganizationUnitPlanFeed, "networkStatus", ""),
        isUnitPlansData: !_.isEmpty(organizationData),
        isUnitPlansLoading: _.includes(
          [1, 2],
          getOrganizationUnitPlanFeed.networkStatus
        ),
        isData: isData && !_.isEmpty(organizationData),
        isLoading:
          isLoading ||
          _.includes([1, 2], getOrganizationUnitPlanFeed.networkStatus),
      };
    },
  })
)(UnitSelection);
